import { cn } from '@/lib/cn';
import { GripHorizontal, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type FloatingWindowProps = {
  title: string;
  subtitle?: string;
  onClose: () => void;
  onFocus?: () => void;
  zIndex?: number;
  initialOffset?: number;
  headerActions?: React.ReactNode;
  headerLeading?: React.ReactNode;
  children: React.ReactNode;
  widthClassName?: string;
};

const WINDOW_WIDTH = 400;
const STAGGER = 28;

function getInitialPosition(offset: number) {
  const maxX = typeof window !== 'undefined' ? window.innerWidth - WINDOW_WIDTH - 24 : 120;
  return {
    x: Math.max(24, Math.min(120 + offset * STAGGER, maxX)),
    y: 72 + offset * STAGGER,
  };
}

export function FloatingWindow({
  title,
  subtitle,
  onClose,
  onFocus,
  zIndex = 50,
  initialOffset = 0,
  headerActions,
  headerLeading,
  children,
  widthClassName = 'w-[400px]',
}: FloatingWindowProps) {
  const [position, setPosition] = useState(() => getInitialPosition(initialOffset));
  const dragState = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    const drag = dragState.current;
    if (!drag || e.pointerId !== drag.pointerId) return;

    const maxX =
      typeof window !== 'undefined' ? window.innerWidth - WINDOW_WIDTH - 8 : drag.originX;
    const maxY =
      typeof window !== 'undefined' ? window.innerHeight - 80 : drag.originY;

    setPosition({
      x: Math.max(8, Math.min(drag.originX + (e.clientX - drag.startX), maxX)),
      y: Math.max(8, Math.min(drag.originY + (e.clientY - drag.startY), maxY)),
    });
  }, []);

  const handlePointerUp = useCallback((e: PointerEvent) => {
    const drag = dragState.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    dragState.current = null;
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
  }, [handlePointerMove]);

  useEffect(() => {
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  function startDrag(e: React.PointerEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).closest('button, a, input, label')) return;

    onFocus?.();
    dragState.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: position.x,
      originY: position.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }

  return createPortal(
    <div
      className={cn('fixed', widthClassName)}
      style={{ left: position.x, top: position.y, zIndex }}
      onMouseDown={onFocus}
      role="dialog"
      aria-label={title}
    >
      <div className="flex max-h-[min(78vh,720px)] flex-col overflow-hidden rounded-2xl border border-manejai-sand/80 bg-white shadow-floating">
        <div
          className="flex cursor-grab items-start gap-2 border-b border-manejai-sand bg-manejai-cream/40 px-4 py-3 active:cursor-grabbing"
          onPointerDown={startDrag}
        >
          <GripHorizontal className="mt-2 h-4 w-4 shrink-0 text-manejai-bark/40" aria-hidden />
          {headerLeading && (
            <div
              className="shrink-0"
              onPointerDown={(e) => e.stopPropagation()}
            >
              {headerLeading}
            </div>
          )}
          <div className="min-w-0 flex-1 self-center">
            <h2 className="truncate font-display text-base font-semibold text-manejai-forest">
              {title}
            </h2>
            {subtitle && (
              <p className="truncate text-xs text-manejai-bark/60">{subtitle}</p>
            )}
          </div>
          {headerActions && (
            <div className="flex shrink-0 items-center" onPointerDown={(e) => e.stopPropagation()}>
              {headerActions}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-manejai-bark/50 transition hover:bg-manejai-sand/60 hover:text-manejai-forest"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
